// @ts-nocheck
import React from 'react';
import { Box, Card, Typography } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import { getNameWithoutPrefix } from '../../../utils/resources/MetadataAvatarType';
import { toFormatDate } from '../../../utils/date';
import CreationDateField from '../../avatarAnc/columns/CreationDateField';
import { alpha, makeStyles } from '@material-ui/core/styles';
import ShowLastNoteIcon1 from '../../showLastNoteIcon/showLastNoteIcon1';
import { MAINTENANCE, SLUDGE_EMPTYING } from '../../../utils/rikutec';
import { useDispatch } from 'react-redux';
import { selectAnc } from '../../../redux/actions/ancs';
import { CRM_COLORS, CRM_RADIUS } from '../../../theme/crmColors';
import { BREAKPOINTS } from '../../../theme/breakpoints';
import { useIsMobile } from '../../../hooks/useIsMobile';

// Past five fields a row gets too narrow for a date and its picto: the rest goes on a second row.
const MAX_DETAIL_COLUMNS = 5;

/** The label / value look of a card line, shared with whatever is unfolded under a card. */
export const detailLabelStyle = {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: CRM_COLORS.text.secondary,
    lineHeight: 1.3,
};

export const detailValueStyle = {
    fontSize: '0.8125rem',
    color: CRM_COLORS.text.primary,
    fontWeight: 500,
    lineHeight: 1.3,
    fontVariantNumeric: 'tabular-nums' as const,
};

const useStyles = makeStyles(() => ({
    modernAncCard: {
        borderRadius: CRM_RADIUS.card,
        boxShadow: CRM_COLORS.shadow,
        border: `1px solid ${CRM_COLORS.border}`,
        backgroundColor: CRM_COLORS.surface,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: CRM_COLORS.shadowHover,
        },
    },
    modernAncCardHeaderCollapsed: {
        padding: '16px 20px',
        borderBottom: 'none',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            alignItems: 'flex-start',
            padding: '12px 16px',
            gap: '12px',
        },
    },
    modernAncInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
    },
    modernAncTitle: {
        fontSize: '1.0625rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: CRM_COLORS.secondary,
        fontVariantNumeric: 'tabular-nums',
        marginBottom: '0px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: '1',
        minWidth: 0,
        marginRight: '12px',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            fontSize: '1rem',
            flexWrap: 'wrap',
            gap: '4px',
            marginRight: 0,
        },
    },
    modernAncSubtitle: {
        fontSize: '0.8rem',
        color: CRM_COLORS.text.secondary,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: '2px',
    },
    pipeSeparator: {
        color: CRM_COLORS.borderStrong,
        fontSize: '0.875rem',
        fontWeight: 400,
        margin: '0 4px',
    },
    modernAncHeaderDetails: {
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
        flexWrap: 'wrap',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            flexDirection: 'column',
            gap: '6px',
            marginTop: '4px',
        },
    },
    // A caller's own fields: equal columns, so the same field lines up from one card to the next.
    modernAncDetailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(var(--detail-columns), minmax(0, 1fr))',
        gap: '12px',
        marginTop: '8px',
        '@media (max-width:900px)': {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: '6px',
            marginTop: '4px',
        },
    },
    modernAncHeaderDetailItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: '100px',
        flex: '1',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
            alignItems: 'baseline',
            gap: '8px',
        },
    },
    modernAncHeaderDetailLabel: detailLabelStyle,
    modernAncHeaderDetailValue: {
        ...detailValueStyle,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    modernAncStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '0px',
        flexShrink: 0,
        marginLeft: 'auto',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            alignSelf: 'flex-start',
        },
    },
    modernStatusChip: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
        textAlign: 'right',
    },
    modernStatusPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 999,
        padding: '3px 10px 3px 8px',
        fontSize: '0.78rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    modernStatusDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
        flexShrink: 0,
    },
    modernStatusDate: {
        fontSize: '0.78rem',
        color: CRM_COLORS.text.secondary,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    },
    modernStatusActive: CRM_COLORS.status.ok,
    modernStatusWarning: CRM_COLORS.status.wait,
    modernStatusError: CRM_COLORS.status.bad,
    // A card with controls: title, status and controls on top, its fields on a row of their own.
    modernAncCardHeaderStacked: {
        flexWrap: 'wrap',
        '& $modernStatusChip': {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
        },
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            '& $modernAncStatus': {
                alignSelf: 'stretch',
                marginLeft: 0,
                justifyContent: 'space-between',
                flexWrap: 'wrap',
            },
            '& $modernStatusChip': {
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 4,
            },
        },
    },
    modernAncFieldsRow: {
        flex: '0 0 100%',
        minWidth: 0,
    },
}));

const getStatusChip = (status, dateOfTakesPlace, classes) => {
    const statusMap = {
        'MISE EN SERVICE': { type: 'active', label: 'En service' },
        'FIN DE MISE EN SERVICE': { type: 'warning', label: 'Fin de service' },
        'BLOQUÉ QUALITÉ': { type: 'error', label: 'Bloqué qualité' },
        REBUT: { type: 'error', label: 'Rebut' },
        'PRÊTE À LIVRER': { type: 'warning', label: 'Prête à livrer' },
        POSER: { type: 'warning', label: 'À poser' },
    };

    const normalized = (status || '').toString().trim().toUpperCase();
    const statusInfo = statusMap[normalized] || { type: 'warning', label: status };
    const statusClass = `modernStatus${statusInfo.type.charAt(0).toUpperCase() + statusInfo.type.slice(1)}`;

    return (
        <Box className={classes.modernStatusChip}>
            <span className={`${classes.modernStatusPill} ${classes[statusClass]}`}>
                <span className={classes.modernStatusDot} aria-hidden />
                {statusInfo.label}
            </span>
            {dateOfTakesPlace && <span className={classes.modernStatusDate}>{toFormatDate(dateOfTakesPlace)}</span>}
        </Box>
    );
};

// Helper to decide whether to show maintenance/vidange related info
const isNormalState = (status) => {
    const normalStates = ['mise en service', 'fin de mise en service'];
    return normalStates.includes(status?.trim().toLowerCase());
};

// This card is tailored for SOURCE_BOARDS (TableView1) flattened fields
const CardItemBoards = ({
    id,
    record,
    // header
    displayId: displayIdProp, // productSerialNumber or fallback
    typeName: typeNameProp, // productTypeName
    productTypeArticle: productTypeArticleProp,
    statusName: statusNameProp,
    statusDate: statusDateProp,
    // common
    creationDate,
    // capacities / characteristics
    totalUsefulVolume,
    processingCapacity,
    sludgeStorageVolume,
    model,
    // flat fields for dates
    productInstallationDate,
    productLastMaintenanceDate,
    productLastEmptyingDate,
    // data objects for icons
    productLastMaintenanceData,
    productLastEmptyingData,
    // other displayed flat fields (optional in header if needed later)
    productTypology,
    productPostalCode,
    showMaintenance = true,
    // optional: fields replacing the default ones, controls beside the status, content under the line
    details,
    headerAction,
    children,
}: any) => {
    const classes = useStyles();
    const t = useTranslate();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    // Prefer values from `record` when provided; fallback to explicit props for backward compatibility
    const typeTitleName = typeNameProp ?? record?.productTypeName;
    const statusName = statusNameProp ?? record?.productCurrentStatusName ?? 'MISE EN SERVICE';
    const statusDate = statusDateProp ?? record?.productCurrentStatusDate;

    const creationDateValue = creationDate ?? record?.creationDate;
    const sludgeStorageVolumeValue = sludgeStorageVolume ?? record?.sludgeStorageVolume;
    const totalUsefulVolumeValue = totalUsefulVolume ?? record?.totalUsefulVolume;
    const modelValue = model ?? record?.model;
    const processingCapacityValue = processingCapacity ?? record?.processingCapacity;
    const productCode = productTypeArticleProp ?? record?.productCode;
    const productInstallationDateValue = productInstallationDate ?? record?.productInstallationDate;
    const productLastMaintenanceDateValue = productLastMaintenanceDate ?? record?.productLastMaintenanceDate;
    const productLastEmptyingDateValue = productLastEmptyingDate ?? record?.productLastEmptyingDate;
    const productLastMaintenanceDataValue = productLastMaintenanceData ?? record?.productLastMaintenanceData;
    const productLastEmptyingDataValue = productLastEmptyingData ?? record?.productLastEmptyingData;

    const extractIdFromAny = (value) => {
        if (!value) return undefined;
        if (typeof value === 'string') {
            const parts = value.split('/');
            return parts[parts.length - 1] || value;
        }
        if (typeof value === 'object') {
            return extractIdFromAny(value.id || value['@id']);
        }
        return undefined;
    };

    const displayId =
        displayIdProp ??
        record?.alphaId ??
        record?.productSerialNumber ??
        record?.originId ??
        `ANC-${extractIdFromAny(id)}`;
    const statusChip = getStatusChip(statusName, statusDate, classes);
    const detailsGrid = details && (
        <Box className={classes.modernAncDetailsGrid} style={{ '--detail-columns': Math.min(details.length, MAX_DETAIL_COLUMNS) } as any}>
            {details.map(({ label, value }) => (
                <Box key={label} className={classes.modernAncHeaderDetailItem} style={{ minWidth: 0 }}>
                    <Typography className={classes.modernAncHeaderDetailLabel}>{label}</Typography>
                    <Typography className={classes.modernAncHeaderDetailValue} component='div'>
                        {value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );

    return (
        <Card key={id} className={classes.modernAncCard} onClick={() => record && dispatch(selectAnc(record))} style={{ cursor: 'pointer' }}>
            <Box
                className={`${classes.modernAncCardHeaderCollapsed} ${
                    headerAction ? classes.modernAncCardHeaderStacked : ''
                }`}
            >
                <Box className={classes.modernAncInfo} style={{ minWidth: 0 }}>
                    {isMobile ? (
                        <>
                            <Typography className={classes.modernAncTitle}>
                                {typeTitleName ? getNameWithoutPrefix(typeTitleName) : 'Type non défini'}
                            </Typography>
                            <Typography className={classes.modernAncSubtitle}>
                                N° {displayId}
                                {productCode && (
                                    <>
                                        <span className={classes.pipeSeparator}>·</span>
                                        {productCode}
                                    </>
                                )}
                            </Typography>
                        </>
                    ) : (
                        <Typography className={classes.modernAncTitle}>
                            {typeTitleName ? getNameWithoutPrefix(typeTitleName) : 'Type non défini'}
                            <span className={classes.pipeSeparator}>|</span>
                            {displayId}
                            <span className={classes.pipeSeparator}>|</span>
                            {productCode || 'N/A'}
                        </Typography>
                    )}

                    {details ? (
                        !headerAction && detailsGrid
                    ) : (
                    <Box className={classes.modernAncHeaderDetails}>
                        <Box className={classes.modernAncHeaderDetailItem}>
                            <Typography className={classes.modernAncHeaderDetailLabel}>
                                {t('app.objects.info.created')}
                            </Typography>
                            <Typography className={classes.modernAncHeaderDetailValue}>
                                <CreationDateField record={{ creationDate: creationDateValue }} />
                            </Typography>
                        </Box>

                        {/* Specs de producto: ocultos en móvil (no aportan al escaneo rápido). */}
                        {!isMobile && (
                        <>
                        <Box className={classes.modernAncHeaderDetailItem}>
                            <Typography className={classes.modernAncHeaderDetailLabel}>
                                {t('app.ancs.sludgeStorageVolume')}
                            </Typography>
                            <Typography className={classes.modernAncHeaderDetailValue}>
                                {sludgeStorageVolumeValue || 'N/A'}
                            </Typography>
                        </Box>

                        <Box className={classes.modernAncHeaderDetailItem}>
                            <Typography className={classes.modernAncHeaderDetailLabel}>Volume totale utile</Typography>
                            <Typography className={classes.modernAncHeaderDetailValue}>
                                {totalUsefulVolumeValue || 'N/A'}
                            </Typography>
                        </Box>

                        <Box className={classes.modernAncHeaderDetailItem}>
                            <Typography className={classes.modernAncHeaderDetailLabel}>Modèle</Typography>
                            <Typography className={classes.modernAncHeaderDetailValue}>
                                {modelValue || 'N/A'}
                            </Typography>
                        </Box>

                        <Box className={classes.modernAncHeaderDetailItem}>
                            <Typography className={classes.modernAncHeaderDetailLabel}>
                                Capacité de traitement
                            </Typography>
                            <Typography className={classes.modernAncHeaderDetailValue}>
                                {processingCapacityValue || 'N/A'}
                            </Typography>
                        </Box>
                        </>
                        )}

                        {showMaintenance && isNormalState(statusName) && (
                            <>
                                <Box className={classes.modernAncHeaderDetailItem}>
                                    <Typography className={classes.modernAncHeaderDetailLabel}>
                                        Date d'installation
                                    </Typography>
                                    <Typography className={classes.modernAncHeaderDetailValue}>
                                        {productInstallationDateValue
                                            ? toFormatDate(productInstallationDateValue)
                                            : 'N/A'}
                                    </Typography>
                                </Box>

                                <Box className={classes.modernAncHeaderDetailItem}>
                                    <Typography className={classes.modernAncHeaderDetailLabel}>Maintenance</Typography>
                                    <Box className={classes.modernAncHeaderDetailValue}>
                                        {productLastMaintenanceDateValue
                                            ? toFormatDate(productLastMaintenanceDateValue)
                                            : ''}
                                        <ShowLastNoteIcon1 record={productLastMaintenanceDataValue} tag={MAINTENANCE} />
                                    </Box>
                                </Box>

                                <Box className={classes.modernAncHeaderDetailItem}>
                                    <Typography className={classes.modernAncHeaderDetailLabel}>Vidange</Typography>
                                    <Box className={classes.modernAncHeaderDetailValue}>
                                        {productLastEmptyingDateValue ? toFormatDate(productLastEmptyingDateValue) : ''}
                                        <ShowLastNoteIcon1
                                            record={productLastEmptyingDataValue}
                                            tag={SLUDGE_EMPTYING}
                                        />
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                    )}
                </Box>

                <Box className={classes.modernAncStatus}>
                    {statusChip}
                    {headerAction}
                </Box>

                {details && headerAction && <Box className={classes.modernAncFieldsRow}>{detailsGrid}</Box>}
            </Box>
            {children}
        </Card>
    );
};

export default CardItemBoards;
